<template>
  <AsidePanel
    :components="components"
    :handleMountedCallback="handleMountedCallback"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :noNotification="true"
    :redirectTo="`/managedSoftware/windows`"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="apps"
      />
    </template>
  </AsidePanel>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import Constants, {
  ItemActionConsts,
  ItemTableConsts,
  ItemTypeConsts,
  SoftwareAppBindingModalConsts,
  SoftwareAppDesiredStatusConsts,
  StoreModelMap,
} from 'client/util/Constants';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import ModalUtil from 'client/AdminPortal/util/ModalUtil';
import SaveReturnValue from 'client/components/AsidePanel/SaveReturnValue';
import SoftwareAppsDetails from 'client/SoftwareApps/SoftwareAppsDetails.vue';
import SoftwareAppsStatusItemTable from 'SoftwareApps/SoftwareAppsStatusItemTable';
import SystemGroupItemTable from 'client/components/ItemTable/SystemGroupItemTable';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable.vue';

const { DeviceItemType, DeviceGroupItemType } = ItemType;
const deviceItem = new DeviceItemType();
const deviceGroupItem = new DeviceGroupItemType();

const { create, update } = ItemActionConsts;
const { softwareApps } = ItemTypeConsts;
const softwareAppsModel = StoreModelMap[softwareApps];
const { success } = Constants;

export default {
  name: 'SoftwareAppsAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
  },

  mixins: [
    AsidePanelPropsMixin,
  ],

  props: {
    itemType: {
      type: String,
      default: softwareApps,
    },
  },

  data() {
    return {
      components: [
        {
          label: 'Details',
          vueComponent: SoftwareAppsDetails,
        },
        {
          itemType: ItemTypeConsts.systemGroup,
          label: 'System Groups',
          headerLabels: {
            [create]: 'Deploy to the following system groups',
            [update]: ' is deployed to the following system groups',
          },
          vueComponent: SystemGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/softwareapps/${this.itemId}/systemgroups` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.systemAsidePanel,
          },
        },
        {
          itemType: ItemTypeConsts.system,
          label: 'Systems',
          headerLabels: {
            [create]: 'Deploy to the following systems',
            [update]: 'is deployed on the following systems',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/softwareapps/${this.itemId}/systems` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.softwareAppsAsidePanel,
            parentOS: ItemTableConsts.parentOSType.windows,
          },
        },
      ],
      itemHasBeenSaved: false,
      errorMessage: 'Please review the Details Tab for missing or invalid information',
    };
  },

  beforeMount() {
    this.updateData();
  },

  beforeRouteLeave(to, from, next) {
    this.handleBeforeRouteLeave(to, from, next);
  },

  methods: {
    ...mapGetters(softwareAppsModel, [
      'hasItemChanged',
      'haveAssociationsChanged',
      'displayName',
      'getInstallStatus',
      'getModifiedAssociations',
      'getTotalAssociations',
      'isItemBeingUpdated',
    ]),
    ...mapActions(softwareAppsModel, [
      'saveItem',
      'bindSoftwareAppToSystems',
      'bindSoftwareAppToSystemGroups',
    ]),

    /**
     * Builds text strings to be used when creating the associations modified modal.
     * @param bindings - modified bindings data
     * @param action - install or uninstall
     * @returns {{
     * devicesAndDeviceGroupsUnboundText: string,
     * devicesBoundText: string,
     * deviceGroupsUnboundText: string,
     * devicesUnboundText: string,
     * devicesAndDeviceGroupsBoundText: string,
     * deviceGroupsBoundText: string}}
     */
    buildBindingsModalBodyParts(bindings, action) {
      const {
        systemsToUnbind,
        systemGroupsToUnbind,
      } = bindings;

      const { boundDeviceTotalCount, boundDeviceGroupTotalCount } = this.getTotalAssociations();

      const deviceGroupLabel = (length) => {
        const { label, labelPlural } = deviceGroupItem;
        const deviceLabel = length === 1 ? label : labelPlural;

        return `**${length}** ${deviceLabel}.`;
      };

      const bindingTextStart = `The managed software **${this.displayName()}** will`;

      const buildBindingDevicesText = (bindingAction, deviceAmount) => `${bindingTextStart} ${bindingAction} **${deviceAmount} Windows device(s)**.`;
      const buildBindingDeviceGroupsText = (bindingAction, deviceGroupAmount) => `${bindingTextStart} ${bindingAction} **Windows device(s)** in ${deviceGroupLabel(deviceGroupAmount)}`;
      const buildBindingDeviceAndDeviceGroupsText = (bindingAction, deviceAmount, deviceGroupAmount, preposition) => `${bindingTextStart} ${bindingAction} **${deviceAmount} Windows device(s)** and ${preposition} **Windows devices** in ${deviceGroupLabel(deviceGroupAmount)}`;

      const {
        bindingPreposition,
        bindingActionLabel,
        unbindingActionLabel,
        unbindingPreposition,
      } = SoftwareAppBindingModalConsts[action];

      const devicesAndDeviceGroupsBoundText = buildBindingDeviceAndDeviceGroupsText(
        bindingActionLabel,
        boundDeviceTotalCount,
        boundDeviceGroupTotalCount,
        bindingPreposition,
      );
      const devicesBoundText = buildBindingDevicesText(
        bindingActionLabel,
        boundDeviceTotalCount,
      );
      const deviceGroupsBoundText = buildBindingDeviceGroupsText(
        bindingActionLabel,
        boundDeviceGroupTotalCount,
      );

      const devicesAndDeviceGroupsUnboundText = buildBindingDeviceAndDeviceGroupsText(
        unbindingActionLabel,
        systemsToUnbind.length,
        systemGroupsToUnbind.length,
        unbindingPreposition,
      );
      const devicesUnboundText = buildBindingDevicesText(
        unbindingActionLabel,
        systemsToUnbind.length,
      );
      const deviceGroupsUnboundText = buildBindingDeviceGroupsText(
        unbindingActionLabel,
        systemGroupsToUnbind.length,
      );

      return {
        devicesAndDeviceGroupsBoundText,
        devicesBoundText,
        deviceGroupsBoundText,
        devicesAndDeviceGroupsUnboundText,
        devicesUnboundText,
        deviceGroupsUnboundText,
      };
    },

    confirmBindings() {
      const bindings = this.getModifiedAssociations();
      const mdTemplate = this.getBindingsModalBodyText(bindings);

      return new Promise((resolve) => {
        ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            mdTemplate,
            headerText: 'Managed Software Associations',
            continueText: 'save',
            handleContinue: () => {
              ModalEventBus.close();
              resolve(true);
            },
            handleCancel: () => {
              ModalEventBus.close();
              resolve(false);
            },
          },
        });
      });
    },

    /**
     * When bindings are modified we need to show a custom message to the users
     * displaying the number of associations made or deleted.
     * Each text piece is generated using the buildBindingsModalBodyParts helper function
     * @param bindings - modified bindings data
     * @returns {string} - complete text to be shown in the bindings modal
     */
    getBindingsModalBodyText(bindings) {
      const {
        systemsToBind,
        systemGroupsToBind,
        systemsToUnbind,
        systemGroupsToUnbind,
      } = bindings;
      let boundText = '';
      let unboundText = '';

      const { bindingAction } = SoftwareAppDesiredStatusConsts[this.getInstallStatus()];
      const { bindingInfoText } = SoftwareAppBindingModalConsts[bindingAction];

      const {
        devicesAndDeviceGroupsBoundText,
        devicesBoundText,
        deviceGroupsBoundText,
        devicesAndDeviceGroupsUnboundText,
        devicesUnboundText,
        deviceGroupsUnboundText,
      } = this.buildBindingsModalBodyParts(bindings, bindingAction);

      if (systemsToBind.length && systemGroupsToBind.length) {
        boundText = devicesAndDeviceGroupsBoundText;
      } else {
        boundText = systemsToBind.length ? devicesBoundText : boundText;
        boundText = systemGroupsToBind.length ? deviceGroupsBoundText : boundText;
      }
      boundText = boundText ? `${boundText}\n\n${bindingInfoText}\n\n` : boundText;

      if (systemsToUnbind.length && systemGroupsToUnbind.length) {
        unboundText = devicesAndDeviceGroupsUnboundText;
      } else {
        unboundText = systemsToUnbind.length ? devicesUnboundText : unboundText;
        unboundText = systemGroupsToUnbind.length ? deviceGroupsUnboundText : unboundText;
      }
      unboundText = unboundText ? `${unboundText}\n\nNote that this does not remove or uninstall the software from the ${deviceGroupItem.label}.` : unboundText;

      return `${boundText}${unboundText}`;
    },

    handleBeforeRouteLeave(to, from, next) {
      if (this.hasModelChanged()) {
        ModalUtil.unSavedChangesModal(next);
      } else {
        next();
      }
    },

    handleMountedCallback() {
      if (this.isItemBeingUpdated()) {
        this.components.push(
          {
            label: 'Status',
            vueComponent: SoftwareAppsStatusItemTable,
            vueComponentProps: {
              softwareAppsId: this.itemId,
            },
          },
        );
      }
    },

    async handleSave() {
      const validator = this.$validator;
      await validator.validate();

      if (validator.errors.any()) {
        throw Error(this.errorMessage);
      }

      this.itemHasBeenSaved = true;

      if (this.haveAssociationsChanged()) {
        const canContinue = await this.confirmBindings();

        if (canContinue === false) {
          return new SaveReturnValue({ canContinue });
        }
      }

      return this.saveItem()
        .then(() => this.bindSoftwareAppToSystemGroups())
        .then(() => this.bindSoftwareAppToSystems())
        .then(() => this.notifySaved())
        .catch((error) => {
          throw error instanceof Error ? error : Error('Unknown error');
        });
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }
      return this.hasItemChanged() || this.haveAssociationsChanged();
    },

    notifySaved() {
      const appName = this.displayName() ? this.displayName() : 'Managed Software';
      const installStatus = this.getInstallStatus();
      const statusMessage = SoftwareAppDesiredStatusConsts[installStatus].notificationLabel;

      notify(success, `<strong>${appName}</strong> saved successfully!<br>${statusMessage}`);
    },

    updateData() {
      const deviceGroupsComponent = this.components[1];
      const devicesComponent = this.components[2];

      const {
        label, labelPlural, titlePlural, type,
      } = deviceItem;

      deviceGroupsComponent.itemType = deviceGroupItem.type;
      deviceGroupsComponent.label = deviceGroupItem.titlePlural;
      deviceGroupsComponent.headerLabels = {
        [create]: `Deploy to the following ${label} groups. Software is only installed on Windows ${labelPlural}`,
        [update]: ` is deployed to the following ${label} groups. Software is only installed on Windows ${labelPlural}`,
      };

      devicesComponent.itemType = type;
      devicesComponent.label = titlePlural;
      devicesComponent.headerLabels = {
        [create]: `Deploy to the following ${labelPlural}`,
        [update]: `is deployed on the following ${labelPlural}`,
      };
    },
  },
};
</script>
